import { FC } from 'react';
import { CombinedGraphQLErrors, ErrorLike } from '@apollo/client';
import { motion } from 'motion/react';
import { Button, Stack, SxProps, Theme, Typography } from '@mui/material';
import Iconify from './Iconify';
import useLocales from '../hooks/useLocales';
import { fadeContainerVariants, fadeVariants } from '../utils/animations';

interface ErrorRetryProps {
    error?: ErrorLike | Error | string | null;
    onRetry?: () => void;
    sx?: SxProps<Theme>;
}

const resolveMessage = (error: ErrorRetryProps['error']): string | undefined => {
    if (!error) return undefined;
    if (typeof error === 'string') return error;
    if (CombinedGraphQLErrors.is(error)) {
        return error.errors?.[0]?.message || error.message;
    }
    return error.message;
};

const ErrorRetry: FC<ErrorRetryProps> = ({ error, onRetry, sx }) => {
    const { translate } = useLocales();

    return (
        <Stack
            component={motion.div}
            variants={fadeContainerVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            role="alert"
            sx={{
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                gap: 1,
                flex: 1,
                p: 3,
                ...sx,
            }}
        >
            <motion.div variants={fadeVariants}>
                <Iconify
                    icon="eva:alert-circle-outline"
                    width={32}
                    height={32}
                    sx={{ color: 'error.main', mb: 1 }}
                />
            </motion.div>
            <Typography
                component={motion.p}
                variants={fadeVariants}
                variant="body2"
                color="text.secondary"
                sx={{ wordBreak: 'break-word', m: 0 }}
            >
                {resolveMessage(error) ?? translate('components.errorDisplay.defaultMessage')}
            </Typography>
            {onRetry && (
                <Button
                    component={motion.button}
                    variants={fadeVariants}
                    variant="contained"
                    size="small"
                    onClick={onRetry}
                    startIcon={<Iconify icon="eva:refresh-outline" width={18} height={18} />}
                >
                    {translate('components.errorDisplay.tryAgain')}
                </Button>
            )}
        </Stack>
    );
};

export default ErrorRetry;
